import { CompanyResponse } from "./company.type";
import type { RoleRef } from "./role.type";
import i18next from "../i18n";

export enum UserType {
  BUYER = "BUYER",
  SELLER = "SELLER",
  ADMIN = "ADMIN",
}

export enum UserRole {
  NEW = "NEW",
  USER = "USER",
  BUYER = "BUYER",
  SELLER = "SELLER",
  ADMIN = "ADMIN",
  RESET_PASSWORD = "RESET_PASSWORD",
  BLOCKED = "BLOCKED",
  ADMIN_APPROVAL = "ADMIN_APPROVAL",
  PENDING_REGISTRATION = "PENDING_REGISTRATION",
  PENDING = "PENDING",
}

export enum UserCompanyType {
  BUYER = "BUYER",
  MANAGER = "MANAGER",
  ANALYST = "ANALYST",
  VISUALIZER = "VISUALIZER",
  COMMERCIAL = "COMMERCIAL",
  REQUESTER = "REQUESTER",
  DEFAULT = "DEFAULT",
  ADMIN = "ADMIN",
}

export const userCompanyTypeMap = {
  [UserCompanyType.BUYER]: i18next.t("userTypes.buyer"),
  [UserCompanyType.MANAGER]: i18next.t("userTypes.manager"),
  [UserCompanyType.ANALYST]: i18next.t("userTypes.analyst"),
  [UserCompanyType.VISUALIZER]: i18next.t("userTypes.visualizer"),
  [UserCompanyType.REQUESTER]: i18next.t("userTypes.requester"),
  [UserCompanyType.ADMIN]: i18next.t("userTypes.admin"),
  [UserCompanyType.COMMERCIAL]: i18next.t("userTypes.commercial"),
};

export interface UserResponse {
  createdAt: string; // ISO 8601 date string
  updatedAt: string; // ISO 8601 date string
  id: string; // UUID
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  imageUrl: string | null;
  type: UserType;
  userCompanyType: UserCompanyType;
  role: UserRole;
  localization: string; // Could be a language code like "es" or "en"
  company: CompanyResponse;
  parent?: UserResponse; // Assuming this refers to a parent user or entity
  active: boolean;
  compliant: boolean;
}

/**
 * What `UserDetailResponse` (backend) actually sends for `GET /user/company`,
 * `/user/company/paginated` and `/user/company/user/{userId}` — the roster/detail reads behind
 * Users.tsx, User.tsx, EditUser.tsx and the responsible/delegate/buyer pickers.
 *
 * Deliberately not `UserResponse`: that type is shared with endpoints that still return the full
 * entity (compliant, localization, createdAt/updatedAt, type, a recursive parent, a full
 * CompanyResponse). None of those fields are on the wire for this DTO, so typing these hooks as
 * `UserResponse` would let a future consumer write `user.company.taxId`, compile cleanly, and
 * render blank.
 *
 * `active` IS on the wire here (added alongside the Users list's Status column): unlike
 * `UserUpdateResponse` (deliberately active-less — it backs the general-purpose PUT that must
 * never round-trip a stale activation state, mutated only through PUT /user/{id}/active), this
 * is a read-only DTO and `searchCompanyUsers` does not filter deactivated colleagues out of the
 * page, so the roster needs a way to say which rows they are.
 */
export interface UserCompanyDetail {
  id: string; // UUID
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  imageUrl: string | null;
  role: UserRole;
  userCompanyType: UserCompanyType;
  company: { id: string; name: string };
  parent?: { id: string; fullName: string };
  active: boolean;
  /** RBAC roles assigned to the user (additive; absent on older backends). */
  roles?: RoleRef[];
}

/** Shape of `GET /user/me`. The backend serializes its `Set<String>` abilities as a JSON array. */
export interface MeResponse {
  user: UserResponse;
  companyList: CompanyResponse[];
  abilities: string[];
  /** Per-company Purchase Orders (OC) module flag. Absent on older backends (treated as false). */
  purchaseOrderModuleEnabled?: boolean;
}
